// import { Component } from '@theme/component';
// import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

// /**
//  * A custom element that renders a media gallery.
//  *
//  * @typedef {object} Refs
//  * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
//  * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
//  * @property {HTMLElement[]} [media] - The media elements.
//  *
//  * @extends Component<Refs>
//  */
// export class MediaGallery extends Component {
//   connectedCallback() {
//     super.connectedCallback();

//     const { signal } = this.#controller;
//     const target = this.closest('.shopify-section, dialog');

//     target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
//     this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
//       signal,
//     });
//   }

//   #controller = new AbortController();

//   disconnectedCallback() {
//     super.disconnectedCallback();

//     this.#controller.abort();
//   }

//   /**
//    * Handles a variant update event by replacing the current media gallery with a new one.
//    *
//    * @param {VariantUpdateEvent} event - The variant update event.
//    */
//   #handleVariantUpdate = (event) => {
//     const source = event.detail.data.html;

//     if (!source) return;
//     const newMediaGallery = source.querySelector('media-gallery');

//     if (!newMediaGallery) return;

//     this.replaceWith(newMediaGallery);
//   };

//   /**
//    * Handles the 'zoom-media:selected' event.
//    * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
//    */
//   #handleZoomMediaSelected = async (event) => {
//     this.slideshow?.select(event.detail.index, undefined, { animate: false });
//   };

//   /**
//    * Zooms the media gallery.
//    *
//    * @param {number} index - The index of the media to zoom.
//    * @param {PointerEvent} event - The pointer event.
//    */
//   zoom(index, event) {
//     this.refs.zoomDialogComponent?.open(index, event);
//   }

//   get slideshow() {
//     return this.refs.slideshow;
//   }

//   get media() {
//     return this.refs.media;
//   }

//   get presentation() {
//     return this.dataset.presentation;
//   }
// }

// if (!customElements.get('media-gallery')) {
//   customElements.define('media-gallery', MediaGallery);
// }

import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.slideshow?.addEventListener('slideshow:select', this.#handleSlideshowSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    requestAnimationFrame(() => {
      const selectedMediaId = this.dataset.selectedMediaId || this.#getSelectedVariantMediaId();
      const selectedIndex = this.#preferredVisibleSlideIndex(selectedMediaId);
      this.#ensureMobileCarouselScrollable();
      this.#syncVisibleControls(selectedIndex);
      if (this.querySelector('slideshow-slide[hidden]')) {
        this.#selectVisibleSlide(selectedIndex, selectedMediaId);
      }
      this.#preloadHiddenImages();
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    const preferredMediaId = event.detail.resource?.featured_media?.id;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) {
      // Ignore optimistic/non-gallery updates.
      // Applying local hide/show filtering here causes slideshow state drift.
      return;
    }

    // The fetched gallery already points at the selected variant's media.
    // Carrying the previous color's media id into the replacement can leave
    // Safari positioned on a slide that is now hidden.
    if (preferredMediaId) {
      newMediaGallery.dataset.selectedMediaId = String(preferredMediaId);
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Keeps the visible thumbnail state aligned when the slideshow changes by
   * arrow, drag, or a media-id thumbnail click.
   *
   * @param {CustomEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const mediaId = this.#getMediaIdFromSlideshowEvent(event);
    if (!mediaId) return;

    this.#syncVisibleControlsByMediaId(mediaId, { scroll: true });
    requestAnimationFrame(() => this.#syncVisibleControlsByMediaId(mediaId, { scroll: true }));
  };

  /**
   * Slideshow select events may expose slide-id in `detail.id`, so resolve
   * media-id from event detail/slide to keep gallery state stable.
   *
   * @param {CustomEvent} event
   * @returns {string}
   */
  #getMediaIdFromSlideshowEvent(event) {
    const explicitMediaId = event.detail?.mediaId;
    if (explicitMediaId) return String(explicitMediaId);

    const selectedSlide = event.detail?.slide;
    const slideMediaId = selectedSlide?.getAttribute?.('data-media-id');
    if (slideMediaId) return String(slideMediaId);

    const slideId = event.detail?.id;
    if (!slideId) return '';

    const match = [...this.querySelectorAll('slideshow-slide')].find(
      (slide) => slide.getAttribute('slide-id') === String(slideId)
    );
    return match?.getAttribute('data-media-id') || '';
  }

  /**
   * Selects a product media item from rendered controls.
   *
   * @param {string | number} mediaId
   * @param {PointerEvent} [event]
   */
  async selectByMediaId(mediaId, event) {
    event?.preventDefault();
    event?.stopPropagation();
    event?.stopImmediatePropagation?.();

    const slide = [...this.querySelectorAll('slideshow-slide')].find(
      (item) => item.getAttribute('data-media-id') === String(mediaId)
    );
    const slideId = slide?.getAttribute('slide-id');

    if (!slideId) return;

    this.dataset.selectedMediaId = String(mediaId);
    this.#syncVisibleControlsByMediaId(mediaId, { scroll: false });
    await this.slideshow?.select({ id: slideId }, event, { animate: false });
    this.#syncVisibleControlsByMediaId(mediaId, { scroll: false });
    requestAnimationFrame(() => this.#syncVisibleControlsByMediaId(mediaId, { scroll: false }));
  }

  /**
   * @param {string} color
   * @param {string | number} [preferredMediaId]
   */
  #filterByColor(color, preferredMediaId) {
    const normalizedColor = this.#normalize(color);
    if (!normalizedColor) return;

    const filterItems = [
      ...this.querySelectorAll('slideshow-slide, .media-gallery__grid > li, .dialog-zoomed-gallery > li'),
    ];
    const controlItems = [
      ...this.querySelectorAll(
        '.slideshow-controls__thumbnail, .slideshow-controls__dots .slideshow-control, .dialog-thumbnails-list__thumbnail'
      ),
    ];

    const allItems = [...filterItems, ...controlItems];
    const mappedColors = allItems.flatMap((item) => this.#getMediaColors(item));
    const alts = allItems.map((item) => this.#getMediaAlt(item)).filter((alt) => alt && !this.#isGlobalAlt(alt));
    const hasExactMatch = alts.some((alt) => this.#isExactColorMatch(alt, normalizedColor)) || mappedColors.includes(normalizedColor);
    const hasLooseMatch =
      !hasExactMatch &&
      (alts.some((alt) => this.#isLooseColorMatch(alt, normalizedColor)) ||
        mappedColors.some((mappedColor) => this.#isLooseColorMatch(mappedColor, normalizedColor)));

    for (const item of allItems) {
      const alt = this.#getMediaAlt(item);
      const mappedItemColors = this.#getMediaColors(item);
      const shouldShow =
        !hasExactMatch && !hasLooseMatch
          ? true
          : this.#isGlobalAlt(alt) ||
            (hasExactMatch
              ? this.#isExactColorMatch(alt, normalizedColor) || mappedItemColors.includes(normalizedColor)
              : this.#isLooseColorMatch(alt, normalizedColor) ||
                mappedItemColors.some((mappedColor) => this.#isLooseColorMatch(mappedColor, normalizedColor)));

      item.toggleAttribute('hidden', !shouldShow);
      if (item.matches('.slideshow-controls__dots .slideshow-control')) {
        item.closest('li')?.toggleAttribute('hidden', !shouldShow);
      }
    }

    const slides = [...this.querySelectorAll('slideshow-slide')];
    const visibleSlides = slides.filter((slide) => !slide.hasAttribute('hidden'));

    // If filtering leaves only one visible slide, fallback to full gallery.
    // This prevents carousel lock on mobile and snap-back on desktop.
    if (slides.length > 1 && visibleSlides.length < 2) {
      for (const item of allItems) {
        item.removeAttribute('hidden');
        if (item.matches('.slideshow-controls__dots .slideshow-control')) {
          item.closest('li')?.removeAttribute('hidden');
        }
      }
    }

    const firstVisibleIndex = this.#preferredVisibleSlideIndex(preferredMediaId);
    const selectedMediaId = this.querySelectorAll('slideshow-slide')[firstVisibleIndex]?.getAttribute('data-media-id');
    if (selectedMediaId) this.dataset.selectedMediaId = String(selectedMediaId);
    this.#syncVisibleControlsByMediaId(selectedMediaId, { scroll: true });
    this.#selectVisibleSlide(firstVisibleIndex, preferredMediaId);
  }

  #getSelectedColorValue() {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    const inputs = [...(variantPicker?.querySelectorAll('fieldset') || [])];
    const colorFieldset = inputs.find((fieldset) => {
      const legend = fieldset.querySelector('legend')?.textContent || '';
      return /colou?r/i.test(legend);
    });

    const checkedColor = colorFieldset?.querySelector('input:checked');
    if (checkedColor instanceof HTMLInputElement) return checkedColor.value;

    const colorSelect = [...(variantPicker?.querySelectorAll('select') || [])].find((select) =>
      /colou?r/i.test(select.name || select.closest('.variant-option')?.querySelector('label')?.textContent || '')
    );

    return colorSelect?.value || '';
  }

  #getSelectedVariantMediaId() {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    const variantScript = variantPicker?.querySelector('script[data-all-variants]');
    if (!variantScript?.textContent) return undefined;

    let variants;
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch {
      return undefined;
    }

    const selectedValues = [...variantPicker.querySelectorAll('select, fieldset')]
      .map((option) => {
        if (option instanceof HTMLSelectElement) return option.value;
        return option.querySelector('input:checked')?.value || '';
      })
      .filter(Boolean);

    const variant = variants.find((item) =>
      selectedValues.every((value, index) => item[`option${index + 1}`] === value)
    );

    return variant?.featured_media?.id;
  }

  /**
   * @param {Element} item
   */
  #getMediaAlt(item) {
    const rawAlt = item.getAttribute('data-media-alt') || item.querySelector('img')?.getAttribute('alt') || '';
    return this.#normalize(rawAlt);
  }

  /**
   * @param {Element} item
   */
  #getMediaColors(item) {
    return (item.getAttribute('data-media-colors') || '')
      .split('|')
      .map((color) => this.#normalize(color))
      .filter(Boolean);
  }

  /**
   * @param {string} value
   */
  #normalize(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /**
   * @param {string} alt
   */
  #isGlobalAlt(alt) {
    return ['all', 'all color', 'all colors', 'all colour', 'all colours'].includes(alt);
  }

  /**
   * @param {string} value
   * @param {string} color
   */
  #isExactColorMatch(value, color) {
    if (!value || !color) return false;
    if (value === color) return true;

    return value
      .split(/[|,/+;&]+/)
      .map((part) => this.#normalize(part))
      .filter(Boolean)
      .includes(color);
  }

  /**
   * @param {string} value
   * @param {string} color
   */
  #isLooseColorMatch(value, color) {
    if (!value || !color) return false;
    return this.#isExactColorMatch(value, color) || value.includes(color);
  }

  #firstVisibleSlideIndex() {
    const slides = [...this.querySelectorAll('slideshow-slide')];
    const index = slides.findIndex((slide) => !slide.hasAttribute('hidden'));
    return index < 0 ? 0 : index;
  }

  /**
   * @param {string | number} mediaId
   */
  #globalSlideIndexByMediaId(mediaId) {
    const slides = [...this.querySelectorAll('slideshow-slide')];
    const index = slides.findIndex((slide) => slide.getAttribute('data-media-id') === String(mediaId));
    return index < 0 ? this.#firstVisibleSlideIndex() : index;
  }

  /**
   * @param {string | number} [preferredMediaId]
   */
  #preferredVisibleSlideIndex(preferredMediaId) {
    const slides = [...this.querySelectorAll('slideshow-slide')];
    const preferredMediaIdString = preferredMediaId ? String(preferredMediaId) : '';

    if (preferredMediaIdString) {
      const preferredIndex = slides.findIndex(
        (slide) => !slide.hasAttribute('hidden') && slide.getAttribute('data-media-id') === preferredMediaIdString
      );
      if (preferredIndex >= 0) return preferredIndex;
    }

    const nonGlobalIndex = slides.findIndex(
      (slide) => !slide.hasAttribute('hidden') && !this.#isGlobalAlt(this.#getMediaAlt(slide))
    );
    return nonGlobalIndex >= 0 ? nonGlobalIndex : this.#firstVisibleSlideIndex();
  }

  /**
   * Slideshow numeric selection is based on visible slides, while our gallery
   * indexes include hidden slides. Select by media slide-id to avoid jumps.
   *
   * @param {number} globalIndex
   * @param {string | number} [preferredMediaId]
   */
  #selectVisibleSlide(globalIndex, preferredMediaId) {
    const slides = [...this.querySelectorAll('slideshow-slide')];
    const preferredMediaIdString = preferredMediaId ? String(preferredMediaId) : '';
    const preferredSlide = preferredMediaIdString
      ? slides.find(
          (slide) => !slide.hasAttribute('hidden') && slide.getAttribute('data-media-id') === preferredMediaIdString
        )
      : null;
    const slide = preferredSlide || slides[globalIndex];
    const slideId = slide?.getAttribute('slide-id');
    const mediaId = slide?.getAttribute('data-media-id');

    if (slideId) {
      if (mediaId) this.#syncVisibleControlsByMediaId(mediaId, { scroll: true });
      Promise.resolve(this.slideshow?.select({ id: slideId }, undefined, { animate: false })).then(() => {
        if (mediaId) this.#syncVisibleControlsByMediaId(mediaId, { scroll: true });
      });
      return;
    }

    const visibleIndex = slides.filter((item, index) => index < globalIndex && !item.hasAttribute('hidden')).length;
    this.slideshow?.select(visibleIndex, undefined, { animate: false });
  }

  /**
   * Keep controls tied to their original slide index. Reindexing to visible
   * order breaks selection because hidden slides still exist in the slideshow.
   *
   * @param {number} selectedIndex
   */
  #syncVisibleControls(selectedIndex) {
    const mediaId = this.querySelectorAll('slideshow-slide')[selectedIndex]?.getAttribute('data-media-id');
    if (mediaId) {
      this.#syncVisibleControlsByMediaId(mediaId, { scroll: true });
      return;
    }

    const controls = this.querySelectorAll('slideshow-controls');

    for (const control of controls) {
      const buttons = [
        ...control.querySelectorAll('.slideshow-controls__thumbnail, .slideshow-controls__dots .slideshow-control'),
      ];
      const selectedButton = buttons[selectedIndex];

      for (const [index, button] of buttons.entries()) {
        button.setAttribute('aria-selected', index === selectedIndex && !button.hasAttribute('hidden') ? 'true' : 'false');
      }

      if (selectedButton instanceof HTMLElement && !selectedButton.hasAttribute('hidden')) {
        this.#scrollControlIntoView(selectedButton);
      }
    }
  }

  /**
   * Keeps thumbnails active by media id. This avoids mismatches when color
   * filtering leaves hidden slides in the DOM and slideshow indexes shift.
   *
   * @param {string | number | null | undefined} mediaId
   */
  #syncVisibleControlsByMediaId(mediaId, { scroll = false } = {}) {
    const selectedMediaId = mediaId ? String(mediaId) : '';
    if (!selectedMediaId) return;

    const controls = this.querySelectorAll('slideshow-controls');

    for (const control of controls) {
      const buttons = [
        ...control.querySelectorAll('.slideshow-controls__thumbnail, .slideshow-controls__dots .slideshow-control'),
      ];
      let selectedButton = null;

      for (const button of buttons) {
        const isHidden = button.hasAttribute('hidden') || button.closest('li')?.hasAttribute('hidden');
        const isSelected = button.getAttribute('data-media-id') === selectedMediaId && !isHidden;
        button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
        if (isSelected && button instanceof HTMLElement) selectedButton = button;
      }

      if (selectedButton && scroll) this.#scrollControlIntoView(selectedButton);
    }
  }

  /**
   * Keeps the active thumbnail visible inside horizontal or vertical thumbnail strips.
   *
   * @param {HTMLElement} button
   */
  #scrollControlIntoView(button) {
    const container = button.closest('.slideshow-controls__thumbnails-container, .slideshow-controls__dots');
    if (!(container instanceof HTMLElement)) return;

    const buttonRect = button.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const padding = 8;
    let nextLeft = container.scrollLeft;
    let nextTop = container.scrollTop;

    if (buttonRect.left < containerRect.left + padding) {
      nextLeft += buttonRect.left - containerRect.left - padding;
    } else if (buttonRect.right > containerRect.right - padding) {
      nextLeft += buttonRect.right - containerRect.right + padding;
    }

    if (buttonRect.top < containerRect.top + padding) {
      nextTop += buttonRect.top - containerRect.top - padding;
    } else if (buttonRect.bottom > containerRect.bottom - padding) {
      nextTop += buttonRect.bottom - containerRect.bottom + padding;
    }

    if (nextLeft === container.scrollLeft && nextTop === container.scrollTop) return;

    container.scrollTo({
      left: Math.max(0, nextLeft),
      top: Math.max(0, nextTop),
      behavior: 'auto',
    });
  }

  #preloadHiddenImages() {
    const run = () => {
      const images = this.querySelectorAll('slideshow-slide[hidden] img, .media-gallery__grid > li[hidden] img');

      for (const img of images) {
        if (!(img instanceof HTMLImageElement)) continue;

        img.loading = 'eager';

        const preload = new Image();
        preload.decoding = 'async';
        preload.sizes = img.sizes;
        preload.srcset = img.srcset;
        preload.src = img.currentSrc || img.src;
      }
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      setTimeout(run, 300);
    }
  }

  #ensureMobileCarouselScrollable() {
    if (!window.matchMedia('(max-width: 768px)').matches) return;

    const slides = [...this.querySelectorAll('slideshow-slide')];
    if (slides.length <= 1) return;

    const visibleSlides = slides.filter((slide) => !slide.hasAttribute('hidden'));
    if (visibleSlides.length >= 2) return;

    const revealSelectors = [
      'slideshow-slide',
      '.media-gallery__grid > li',
      '.dialog-zoomed-gallery > li',
      '.slideshow-controls__thumbnail',
      '.slideshow-controls__dots .slideshow-control',
      '.dialog-thumbnails-list__thumbnail',
    ];

    for (const item of this.querySelectorAll(revealSelectors.join(','))) {
      item.removeAttribute('hidden');
      if (item.matches('.slideshow-controls__dots .slideshow-control')) {
        item.closest('li')?.removeAttribute('hidden');
      }
    }
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    event.preventDefault();
    event.stopPropagation();

    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Shares the current product page URL, or copies it to clipboard on unsupported browsers.
   *
   * @param {PointerEvent} event - The pointer event.
   */
  share(event) {
    event.preventDefault();
    event.stopPropagation();

    const url = window.location.href;
    const title = document.title || '';

    if (navigator.share) {
      navigator.share({ title, url }).catch(() => {});
      return;
    }

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        window.alert('Product link copied to clipboard');
      });
      return;
    }

    window.prompt('Copy this link', url);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
